import { createContext, PropsWithChildren, useContext } from "react";
import { Step } from "~/lib/pipeline";
import { Job, RetryableJob } from "~/components/build/Show/lib/types";
import { filter, isNumber, matchesProperty } from "lodash";

type StepDrawerContextValue = {
  step?: Step;
  job?: RetryableJob;
  jobs: Job[];
  retries: RetryableJob[];
};

const StepDrawerContext = createContext<StepDrawerContextValue | null>(null);

export function StepDrawerProvider({
  children,
  ...value
}: PropsWithChildren<StepDrawerContextValue>) {
  return <StepDrawerContext.Provider value={value}>{children}</StepDrawerContext.Provider>;
}

export function useStepDrawer(): StepDrawerContextValue {
  const context = useContext(StepDrawerContext);

  if (!context) {
    throw new Error("useStepDrawer() must be used within a <StepDrawerProvider />");
  }

  return context;
}

/**
 * Filter step jobs to find retries for a specific job.
 * Extracted from useRetriesForJob in BuildContext.tsx as a pure function
 * so it can be called outside of hooks (e.g., in the eager data path).
 */
export function filterRetriesForJob(stepJobs: RetryableJob[], job: RetryableJob): RetryableJob[] {
  let jobs = stepJobs;

  if ("matrix" in job && job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if ("parallelGroupIndex" in job && isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  return jobs;
}
